import React, { useEffect, useMemo, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/Help";
import {
  Controller,
  useForm,
  useFormState,
  useWatch,
  type Control,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  EMPTY_BULK_LIST_FILTER,
  splitCompanyNames,
  type BulkFilterableRow,
  type BulkListFilter,
} from "../utils/bulkListFilter";
import { ALL_MATTERS_FIELDS } from "../utils/checkboxFields";

// 空文字は「指定なし」としてnullに寄せ、全角数字・前後空白は半角化してから数値検証する
const holdingCountFieldSchema = z.preprocess(
  (value) =>
    typeof value === "string" && value.trim() !== ""
      ? value.normalize("NFKC").trim()
      : value === ""
      ? null
      : value,
  z.coerce
    .number({ invalid_type_error: "数値を入力してください" })
    .int()
    .min(0)
    .nullable()
);

const filterFormSchema = z
  .object({
    prefecture: z.string(),
    city: z.string(),
    companyNames: z.array(z.string()),
    holdingCountMin: holdingCountFieldSchema,
    holdingCountMax: holdingCountFieldSchema,
    // チェック群は検証ルールを持たないが、入力項目としてフォームに揃える
    realEstateTypes: z.array(z.string()),
    bookTypes: z.array(z.string()),
  })
  .refine(
    (data) =>
      data.holdingCountMin === null ||
      data.holdingCountMax === null ||
      data.holdingCountMin <= data.holdingCountMax,
    {
      message: "下限は上限以下の値を指定してください",
      path: ["holdingCountMax"],
    }
  );

type FilterFormInput = z.input<typeof filterFormSchema>;
type FilterFormOutput = z.output<typeof filterFormSchema>;

const FILTER_FORM_DEFAULT_VALUES: FilterFormInput = {
  prefecture: "",
  city: "",
  companyNames: [],
  holdingCountMin: "",
  holdingCountMax: "",
  realEstateTypes: [],
  bookTypes: [],
};

const toggleListValue = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

// 事項種別→グリッドのチェック列（field名）の対応。
// チェックした種別のいずれかの列にチェックがある行だけを残す絞り込みに使う。
// 「全部事項」は共担/信託/現在/閉鎖のいずれかにチェックがある行
const BOOK_TYPES = [
  {
    label: "全部事項",
    fields: ALL_MATTERS_FIELDS,
  },
  { label: "所有者事項", fields: ["ownerInfo"] },
  { label: "電子公図", fields: ["electricDrawings"] },
  { label: "地積測量図", fields: ["tisekiDrawings"] },
  { label: "地役権図面", fields: ["tiekiDrawings"] },
  { label: "建物図面", fields: ["buildingDrawings"] },
] as const;

const BOOK_TYPE_LABELS = BOOK_TYPES.map(({ label }) => label);

const ALL_VALUE = "";

interface BulkFilterAccordionProps {
  /** 選択肢の生成元（照合済みの全行）。参照するのは県名・会社名・種別のみ */
  rows: Array<
    Pick<BulkFilterableRow, "prefectureName" | "companyName" | "realEstateType">
  >;
  /** 条件が変わるたびに呼ばれる（入力が検証を通らない間は前の条件を保つ） */
  onApply: (filter: BulkListFilter) => void;
}

// モックの「見出し＋入力欄」を縦に積むレイアウト用
const FieldHeading: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children,
}) => (
  <Typography id={id} sx={{ fontWeight: "bold", mb: 1 }}>
    {children}
  </Typography>
);

const REAL_ESTATE_TYPES_HEADING_ID = "bulk-filter-real-estate-types-heading";
const BOOK_TYPES_HEADING_ID = "bulk-filter-book-types-heading";

// 都道府県の選択欄（「すべて」+ 動的選択肢のプルダウン）
const CPrefectureSelect: React.FC<{
  control: Control<FilterFormInput>;
  options: string[];
  sx: SxProps<Theme>;
}> = ({ control, options, sx }) => (
  <Controller
    name="prefecture"
    control={control}
    render={({ field }) => (
      <TextField
        select
        label="都道府県"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        sx={sx}
      >
        <MenuItem value={ALL_VALUE}>すべて</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    )}
  />
);

// 土地・建物種別、事項種別のチェックボックス群
const FilterCheckboxGroup: React.FC<{
  name: "realEstateTypes" | "bookTypes";
  control: Control<FilterFormInput>;
  labels: readonly string[];
  ariaLabelledBy: string;
}> = ({ name, control, labels, ariaLabelledBy }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <FormGroup row role="group" aria-labelledby={ariaLabelledBy}>
        {labels.map((label) => (
          <FormControlLabel
            key={label}
            control={
              <Checkbox
                checked={field.value.includes(label)}
                onChange={() => {
                  field.onChange(toggleListValue(field.value, label));
                }}
              />
            }
            label={label}
          />
        ))}
      </FormGroup>
    )}
  />
);

/**
 * 絞り込みアコーディオン。
 * 選択肢は照合済みリスト内の値から動的生成する
 */
export const BulkFilterAccordion: React.FC<BulkFilterAccordionProps> = ({
  rows,
  onApply,
}) => {
  // 未チェック＝事項種別では絞り込まない。既定でチェックを入れると
  // 他条件だけ絞りたいときにチェックなし行が消えてしまうため空を既定にする
  const { control, reset } = useForm<
    FilterFormInput,
    unknown,
    FilterFormOutput
  >({
    mode: "all",
    defaultValues: FILTER_FORM_DEFAULT_VALUES,
    resolver: zodResolver(filterFormSchema),
  });
  const { errors } = useFormState({ control });

  const options = useMemo(() => {
    const prefectures = new Set<string>();
    const companyNames = new Set<string>();
    const realEstateTypes = new Set<string>();
    for (const row of rows) {
      prefectures.add(row.prefectureName);
      for (const name of splitCompanyNames(row.companyName)) {
        companyNames.add(name);
      }
      realEstateTypes.add(row.realEstateType);
    }
    return {
      prefectures: [...prefectures],
      companyNames: [...companyNames],
      realEstateTypes: [...realEstateTypes],
    };
  }, [rows]);

  // 入力を初期状態に戻す（条件の反映は下の追従で行う）
  const handleClear = (): void => {
    reset(FILTER_FORM_DEFAULT_VALUES);
  };

  // 絞り込みボタンは置かず、入力のたびに条件を反映する（チェックは絞り込みをまたいで維持されるため
  // 即時に絞っても失われるものがない）。検証を通らない入力（保有物件数の範囲など）は反映しない
  const values = useWatch({ control });
  const appliedKeyRef = useRef(JSON.stringify(EMPTY_BULK_LIST_FILTER));
  useEffect(() => {
    const parsed = filterFormSchema.safeParse(values);
    if (!parsed.success) {
      return;
    }
    const data = parsed.data;
    const bookTypeFields = BOOK_TYPES.filter(({ label }) =>
      data.bookTypes.includes(label)
    ).flatMap(({ fields }) => [...fields]);
    const filter: BulkListFilter = {
      ...EMPTY_BULK_LIST_FILTER,
      prefecture: data.prefecture === ALL_VALUE ? null : data.prefecture,
      city: data.city.trim() === "" ? null : data.city.trim(),
      companyNames: data.companyNames.length === 0 ? null : data.companyNames,
      holdingCountMin: data.holdingCountMin,
      holdingCountMax: data.holdingCountMax,
      realEstateTypes:
        data.realEstateTypes.length === 0 ? null : data.realEstateTypes,
      bookTypeFields: bookTypeFields.length === 0 ? null : bookTypeFields,
    };
    // 値が同じなら再適用しない（親のスナップショット更新と再絞り込みを避ける）
    const key = JSON.stringify(filter);
    if (key === appliedKeyRef.current) {
      return;
    }
    appliedKeyRef.current = key;
    onApply(filter);
  }, [values, onApply]);

  return (
    <Accordion disableGutters sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        絞り込み条件を開く
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
          <Box>
            <FieldHeading>都道府県</FieldHeading>
            <CPrefectureSelect
              control={control}
              options={options.prefectures}
              sx={{ width: 250 }}
            />
          </Box>
          <Box>
            <FieldHeading>市区町村</FieldHeading>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  label="市区町村"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  sx={{ width: 640, maxWidth: "100%" }}
                />
              )}
            />
          </Box>
          <Box>
            <FieldHeading id={REAL_ESTATE_TYPES_HEADING_ID}>
              土地・建物
            </FieldHeading>
            <FilterCheckboxGroup
              name="realEstateTypes"
              control={control}
              labels={options.realEstateTypes}
              ariaLabelledBy={REAL_ESTATE_TYPES_HEADING_ID}
            />
          </Box>
          <Box>
            <FieldHeading>会社名</FieldHeading>
            {/* 会社数が多くても選べるよう、検索入力付きの複数選択にする。未選択＝すべて */}
            <Controller
              name="companyNames"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={options.companyNames}
                  value={field.value}
                  onChange={(_event, value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="会社名"
                      placeholder={field.value.length === 0 ? "すべて" : ""}
                    />
                  )}
                  sx={{ width: 640, maxWidth: "100%" }}
                />
              )}
            />
          </Box>
          <Box>
            <FieldHeading>会社の保有物件数</FieldHeading>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {(
                [
                  {
                    name: "holdingCountMin",
                    placeholder: "下限なし",
                    suffix: "筆以上",
                    ariaLabel: "保有物件数の下限",
                  },
                  {
                    name: "holdingCountMax",
                    placeholder: "上限なし",
                    suffix: "筆以下",
                    ariaLabel: "保有物件数の上限",
                  },
                ] as const
              ).map(({ name, placeholder, suffix, ariaLabel }, i) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field, fieldState }) => (
                    <React.Fragment>
                      {i === 1 && <Typography>〜</Typography>}
                      <TextField
                        placeholder={placeholder}
                        value={field.value}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            field.onChange(e.target.value);
                          }
                        }}
                        onBlur={field.onBlur}
                        error={!!fieldState.error}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          "aria-label": ariaLabel,
                          ...((errors.holdingCountMin ??
                            errors.holdingCountMax) && {
                            "aria-describedby": "holding-count-error",
                          }),
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {suffix}
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: 180 }}
                      />
                    </React.Fragment>
                  )}
                />
              ))}
            </Box>
            {(errors.holdingCountMin ?? errors.holdingCountMax) && (
              <FormHelperText id="holding-count-error" error>
                {(errors.holdingCountMin ?? errors.holdingCountMax)?.message}
              </FormHelperText>
            )}
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <FieldHeading id={BOOK_TYPES_HEADING_ID}>事項種別</FieldHeading>
              <Tooltip
                disableInteractive
                title="チェックした種別の列に取得のチェックが入っている行だけを表示します。判定は条件を変えた時点のチェック状態で行います"
              >
                <IconButton
                  size="small"
                  aria-label="事項種別の絞り込みの説明"
                  // 見出しの下余白(mb:1)ぶんアイコンが下にずれるため合わせる
                  sx={{ mb: 1, p: 0 }}
                >
                  <HelpIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
            <FilterCheckboxGroup
              name="bookTypes"
              control={control}
              labels={BOOK_TYPE_LABELS}
              ariaLabelledBy={BOOK_TYPES_HEADING_ID}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClear}>
            条件をクリア
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
